import { Auth } from 'aws-amplify';
import React, { ChangeEvent, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { Input } from '../../../components';
import { useAppContext } from '../../../contexts/AppContext';
import UnauthenticatedLayout from '../../../overwrites/views/layouts/UnauthenticatedLayout/UnauthenticatedLayout';
import { requiredClientMetadata } from '../../../utils/Amplify';
import { Form, ReSendLink } from '../auth.styles';
import SignInHeader from '../shared/SignInHeader';
import { VerifyButton, VerifySubtitleContainer, VerifySubtitleEmail, VerifySubtitleText } from './VerifyPage.styles';

interface Location {
  state: any;
}

export default function Verify() {
  const navigate = useNavigate();
  const [code, setCode] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const { reloadAuthInfo, showError, showAlert } = useAppContext();
  const { state }: Location = useLocation();
  const { t } = useTranslation();

  useEffect(() => {
    if (!state) {
      const isSignUpAvailable = process.env.REACT_APP_SIGNUP_WITHOUT_INVITE_ENABLE === 'true';
      if (isSignUpAvailable) {
        navigate('/signup', { replace: true });
      } else {
        navigate('/login', { replace: true });
      }
    }
  }, []);

  function handleConfirmationSubmit(event: any) {
    event.preventDefault();

    setLoading(true);
    void (async () => {
      try {
        const sanitizedEmail = state.email.trim().toLowerCase();
        await Auth.confirmSignUp(sanitizedEmail as string, code);
        await Auth.signIn(sanitizedEmail as string, state.password as string, {
          ...requiredClientMetadata,
          'first-login': 'true',
        });
        await reloadAuthInfo();
      } catch (e: any) {
        showError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }

  const resendCode = () => {
    void (async () => {
      const sanitizedEmail = state.email.trim().toLowerCase();

      await Auth.resendSignUp(sanitizedEmail as string);
      showAlert(t('auth.verify.codeSent'));
    })();
  };

  return (
    <UnauthenticatedLayout title={t('auth.verify.title')} header={<SignInHeader />}>
      <Form noValidate onSubmit={handleConfirmationSubmit}>
        <VerifySubtitleContainer>
          <VerifySubtitleText>{t('auth.verify.subtitle')}&nbsp;</VerifySubtitleText>
          <VerifySubtitleEmail>{state?.email}</VerifySubtitleEmail>
        </VerifySubtitleContainer>
        <Input
          name="code"
          required
          fullWidth
          autoFocus
          id="code"
          label={t('auth.common.confirmationCode')}
          value={code}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setCode(e.target.value)}
        />
        <VerifyButton type="submit" disabled={!code} loading={loading}>
          {t('auth.verify.title')}
        </VerifyButton>
        <ReSendLink onClick={resendCode}>{t('auth.verify.link')}</ReSendLink>
      </Form>
    </UnauthenticatedLayout>
  );
}
